import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Church, Copy, Check } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { Button } from "@/shared/components/Button";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { useProtecaoSaida } from "@/shared/hooks/useProtecaoSaida";
import { useAtualizarIgreja, useIgreja } from "@/core/hooks/useIgreja";
import {
  emailContatoValido,
  formatarTelefoneContato,
  soDigitosContato,
  telefoneContatoValido,
  textoContatoIgreja,
} from "@/core/utils/contatoIgreja";

// Limite espelhando a coluna do banco (migration 0016): igrejas.nome varchar(150).
const LIMITE_NOME = 150;

/**
 * Igreja (Núcleo, só admin — 03-Núcleo §3): dados administrativos do
 * container principal. Hoje: nome, contato para pedidos sobre dados pessoais
 * (aparece no aviso do elo Visitantes) e o link de cadastro da igreja.
 * Novos campos (endereço, CNPJ) entram quando houver necessidade real — exigem migration.
 */
export function ConfiguracoesIgreja() {
  const { data: igreja, isLoading, isError } = useIgreja();
  const atualizar = useAtualizarIgreja();
  const navigate = useNavigate();

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState(""); // só dígitos
  const [copiado, setCopiado] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: "sucesso" | "erro"; texto: string } | null>(
    null,
  );

  useEffect(() => {
    if (igreja) {
      setNome(igreja.nome);
      setEmail(igreja.contato_email ?? "");
      setTelefone(igreja.contato_telefone ?? "");
    }
  }, [igreja]);

  const alterado =
    !!igreja &&
    (nome.trim() !== igreja.nome ||
      email.trim() !== (igreja.contato_email ?? "") ||
      telefone !== (igreja.contato_telefone ?? ""));

  const protecao = useProtecaoSaida(() => alterado);

  const linkCadastro = igreja ? `${window.location.origin}/cadastro/${igreja.codigo}` : "";

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(linkCadastro);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setMensagem({ tipo: "erro", texto: "Não foi possível copiar. Selecione o link e copie." });
    }
  }

  async function handleSalvar(e: React.FormEvent) {
    e.preventDefault();
    if (!igreja) return;
    const nomeLimpo = nome.trim();
    if (nomeLimpo.length < 3) {
      setMensagem({ tipo: "erro", texto: "O nome da igreja precisa ter pelo menos 3 caracteres." });
      return;
    }
    const emailLimpo = email.trim();
    if (emailLimpo && !emailContatoValido(emailLimpo)) {
      setMensagem({ tipo: "erro", texto: "O e-mail de contato é inválido." });
      return;
    }
    if (telefone && !telefoneContatoValido(telefone)) {
      setMensagem({ tipo: "erro", texto: "Informe o telefone com DDD (10 ou 11 números)." });
      return;
    }
    try {
      await atualizar.mutateAsync({
        id: igreja.id,
        dados: {
          nome: nomeLimpo,
          contato_email: emailLimpo || null,
          contato_telefone: telefone || null,
        },
      });
      setMensagem({ tipo: "sucesso", texto: "Informações da igreja atualizadas." });
    } catch (erro) {
      setMensagem({
        tipo: "erro",
        texto: erro instanceof Error ? erro.message : "Não foi possível salvar.",
      });
    }
    setTimeout(() => setMensagem(null), 3500);
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 pb-12 pt-8 sm:px-6 sm:pt-10">
      <header className="mb-6">
        <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">Igreja</h1>
        <p className="mt-1 text-sm text-muted">Informações administrativas da igreja.</p>
      </header>

      {mensagem && (
        <p
          role="alert"
          className={`mb-4 rounded-xl px-4 py-3 text-sm font-medium ${
            mensagem.tipo === "sucesso" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
          }`}
        >
          {mensagem.texto}
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-muted">Carregando...</p>
      ) : isError || !igreja ? (
        <p role="alert" className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
          Não foi possível carregar as informações da igreja.
        </p>
      ) : (
        <section className="rounded-2xl border border-border bg-surface p-5">
          <div className="flex items-center gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
              <Church size={24} />
            </span>
            <div>
              <h2 className="text-base font-bold text-text">{igreja.nome}</h2>
              <p className="mt-0.5 text-xs text-muted">
                Cadastrada em {new Date(igreja.created_at).toLocaleDateString("pt-BR")}
              </p>
            </div>
          </div>

          <form onSubmit={handleSalvar} className="mt-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Input
                label="Nome da igreja"
                name="nome-igreja"
                value={nome}
                maxLength={LIMITE_NOME}
                onChange={(e) => setNome(e.target.value)}
              />
              <p
                className={`text-right text-xs ${
                  nome.length >= LIMITE_NOME ? "font-medium text-warning" : "text-muted"
                }`}
              >
                {nome.length}/{LIMITE_NOME}
              </p>
            </div>

            <fieldset className="flex flex-col gap-3 rounded-xl border border-border p-4">
              <legend className="px-1 text-sm font-bold text-text">
                Contato para pedidos sobre dados pessoais
              </legend>
              <p className="text-xs leading-relaxed text-muted">
                É para onde o visitante escreve quando quiser ver, corrigir ou apagar os dados dele.
                Aparece no aviso que o voluntário mostra no cadastro. Preencha pelo menos um dos
                dois.
              </p>
              <Input
                label="E-mail de contato"
                name="contato-email"
                type="email"
                maxLength={100}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Telefone ou WhatsApp de contato"
                name="contato-telefone"
                inputMode="tel"
                placeholder="(00) 00000-0000"
                value={formatarTelefoneContato(telefone)}
                onChange={(e) => setTelefone(soDigitosContato(e.target.value))}
              />
              <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
                <span className="font-semibold text-text">
                  Como aparece no aviso ao visitante:{" "}
                </span>
                “Ela pode pedir para apagar quando quiser,{" "}
                {textoContatoIgreja({ email: email.trim() || null, telefone: telefone || null })}.”
              </p>
              {!email.trim() && !telefone && (
                <p role="status" className="text-xs font-medium text-warning">
                  Enquanto estes campos estiverem vazios, o aviso mostra “falando com a liderança da
                  igreja”. Recomendamos preencher.
                </p>
              )}
            </fieldset>

            <Button type="submit" carregando={atualizar.isPending} disabled={!alterado}>
              Salvar
            </Button>
            <BotaoVoltar
              onClick={() => navigate("/nucleo")}
              disabled={atualizar.isPending}
              className="-mt-2"
            />
          </form>
          <ModalSairSemSalvar
            aberto={protecao.pedindoSaida}
            onSair={protecao.confirmarSaida}
            onContinuar={protecao.continuarEditando}
          />

          <div className="mt-6 flex flex-col gap-2 rounded-xl border border-border p-4">
            <h3 className="text-sm font-bold text-text">Link de cadastro da igreja</h3>
            <p className="text-xs leading-relaxed text-muted">
              Envie este link para as pessoas se cadastrarem na {igreja.nome}. O acesso continua
              dependendo da sua aprovação.
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                aria-label="Link de cadastro da igreja"
                value={linkCadastro}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-md bg-surface-2 px-3 py-2 text-xs text-text"
              />
              <button
                type="button"
                onClick={copiarLink}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10"
              >
                {copiado ? <Check size={14} /> : <Copy size={14} />}
                {copiado ? "Copiado" : "Copiar"}
              </button>
            </div>
          </div>

          <p className="mt-4 rounded-md bg-primary/10 px-3 py-2 text-xs leading-relaxed text-primary">
            Precisa registrar endereço ou CNPJ? Esses campos podem ser adicionados quando houver
            necessidade — fale com quem mantém a plataforma.
          </p>
        </section>
      )}
    </main>
  );
}
